'use client';

import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import { FC, SyntheticEvent, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import AiVideosIcon from '@/public/icons/ai_videos.svg';
import EffectIcon from '@/public/icons/local_fire_department.svg';
import StyleIcon from '@/public/icons/person.svg';
import PhotoIcon from '@/public/icons/photo_camera_back.svg';
import VideoIcon from '@/public/icons/play_circle.svg';
import BestIcon from '@/public/icons/star.svg';
import { Header } from '@/src/components/Header';
import { PackPreviewSeeAll } from '@/src/components/PackPreviewSeeAll';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
  useMobileContext,
} from '@/src/providers';
import { getBlockName } from '@/src/utils/getBlockName';
import { getImageByGender } from '@/src/utils/getImageByGender';

const styles = {
  portal:
    '!h-dvh !max-h-none !min-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'flex gap-2 items-center mb-4',
  headWrap:
    'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  icon: 'w-5 h-5 fill-current',
  iconStyle: 'w-4 h-4 fill-current',
  title: 'text-base font-bold',
  name: 'text-base font-bold mb-3',
  wrapper:
    'grid grid-cols-2 row-auto gap-x-4 gap-y-3 justify-between bg-fill-menu pb-5',
  wrapperVideo: 'grid-cols-1',
  innerWrap: 'shrink-0 bg-fill-menu px-5 pt-16',
  styleItem: 'shrink-0 relative overflow-hidden rounded-lg',
  preview: 'w-full h-[180px] object-cover object-top',
  tag: 'absolute top-1 right-1',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-sm',
  info: 'absolute left-3 bottom-3',
  styleName: 'mb-[2px] text-sm font-medium text-left',
  styleCount: 'text-sm font-medium text-label-secondary text-left',
  closeButton: 'absolute top-7 z-2',
  closeIcon: 'w-6 h-6',
};

export const SeeAllImagesPortal: FC = () => {
  const profile = useProfileContext()!;
  const sendGTM = useGTMContext();
  const {
    activeCategory,
    activeStyle,
    changeActiveStyle,
    genderMode,
    portalSeeAllMode,
    setPrevPortal,
    currentPack,
    prevPortal,
  } = useStyleContext();
  const { openPortal, closePortal } = usePortalContext();
  const t = useTranslations('MenuPage');
  const { isMobiles } = useMobileContext();

  const portalHeader = useMemo(() => {
    switch (portalSeeAllMode) {
      case 'styles':
        return (
          <>
            <div className={styles.head}>
              <StyleIcon className={styles.iconStyle} />
              <div className={styles.title}>{t('stylesTitle')}</div>
              <div className={styles.styleCount}>{t('photosCount')}</div>
            </div>
            <div className={styles.name}>
              {activeCategory?.name[profile?.lang]}
            </div>
          </>
        );
      case 'effectsCategories':
        return (
          <>
            <div className={styles.head}>
              <EffectIcon className={styles.iconStyle} />
              <div className={styles.title}>{t('effectsTitle')}</div>
              <div className={styles.styleCount}>{t('effectCount')}</div>
            </div>
            <div className={styles.name}>
              {activeCategory?.name[profile?.lang]}
            </div>
          </>
        );
      case 'videoStyleCategories':
        return (
          <>
            <div className={styles.head}>
              <AiVideosIcon className={styles.iconStyle} />
              <div className={styles.title}>
                {activeCategory?.name[profile?.lang]}
              </div>
            </div>
          </>
        );
      case 'photoSessions':
        return (
          <div className={styles.head}>
            <PhotoIcon className={styles.icon} />
            <div className={styles.title}>{t('photoshootTitle')}</div>
            <div className={styles.styleCount}>
              {t('photoshootPhotosCount')}
            </div>
          </div>
        );
      case 'videoLooks':
        return (
          <div className={styles.head}>
            <VideoIcon className={styles.icon} />
            <div className={styles.title}>{t('videoTitle')}</div>
          </div>
        );
      case 'videoStyles':
        return (
          <div className={styles.head}>
            <VideoIcon className={styles.icon} />
            <div className={styles.title}>{t('animatePhotoTitle')}</div>
          </div>
        );
      case 'aiVideos':
        return (
          <div className={styles.head}>
            <AiVideosIcon className={styles.icon} />
            <div className={styles.title}>{t('videoTitle')}</div>
          </div>
        );
      case 'combiVideos':
        return (
          <div className={styles.head}>
            <VideoIcon className={styles.icon} />
            <div className={styles.title}>{t('combiVideoTitle')}</div>
          </div>
        );
      case 'videoVeo3':
        return (
          <div className={styles.head}>
            <AiVideosIcon className={styles.icon} />
            <div className={styles.title}>{t('videoVeo3')}</div>
          </div>
        );
      case 'storyLines':
        return (
          <div className={styles.head}>
            <AiVideosIcon className={styles.icon} />
            <div className={styles.title}>{t('storyLines')}</div>
          </div>
        );
      case 'bestItems':
        return (
          <div className={styles.head}>
            <BestIcon className={styles.icon} />
            <div className={styles.title}>{t('bestTitle')}</div>
          </div>
        );
      case 'limited':
        return (
          <div className={styles.head}>
            <div className={styles.title}>{t('limitedTitle')}</div>
          </div>
        );
      default:
        return;
    }
  }, [activeCategory?.name, portalSeeAllMode, profile?.lang, t]);

  const handleClickImage = (event: SyntheticEvent) => {
    const styleId = event.currentTarget.getAttribute('data-style-id');
    const currentIndex = event.currentTarget.getAttribute('data-style-ind');
    let position;

    if (styleId) {
      changeActiveStyle(styleId, portalSeeAllMode);

      if (currentIndex) {
        position = Number(currentIndex);
      }

      const currentStyle = currentPack?.find((item) => item.id === styleId);

      sendGTM({
        event: 'menu_selection',
        type: currentStyle?.__typename!,
        block: getBlockName(portalSeeAllMode),
        category_name: [
          'styles',
          'effectsCategories',
          'videoStyleCategories',
        ].includes(portalSeeAllMode)
          ? activeCategory?.name['en']
          : null,
        name: currentStyle?.name['en'],
        category_position: position,
        gender: genderMode,
      });
    }

    setPrevPortal('seeAll');
    closePortal();
    openPortal(PORTALS.GenerateImage);
  };

  const handleClosePortal = () => {
    if (prevPortal === 'trending') {
      closePortal();
      openPortal(PORTALS.SeeAllTrending);
    } else {
      closePortal();
    }
  };

  return (
    <PortalModal
      portalId={PORTALS.SeeAllImages}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.headWrap, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.innerWrap}>
            {portalHeader}
            <div
              className={twMerge(
                styles.wrapper,
                ['videoVeo3', 'storyLines'].includes(portalSeeAllMode) &&
                  styles.wrapperVideo,
              )}
            >
              {currentPack?.map((item, i) => (
                <PackPreviewSeeAll
                  key={item.id}
                  id={item.id}
                  index={i}
                  isPopular={item.isPopular}
                  isNew={item.isNew}
                  name={item.name[profile?.lang]}
                  image={getImageByGender(item, genderMode)!}
                  handleClickImage={handleClickImage}
                  isVideo={
                    [
                      'VideoLook',
                      'VideoStyle',
                      'CombiVideo',
                      'VideoVeo3',
                      'StoryLine',
                    ]?.includes(item.__typename!) ||
                    getImageByGender(item, genderMode)?.includes('.mp4') ||
                    false
                  }
                  isHorizontalVideo={['VideoVeo3', 'StoryLine'].includes(
                    item.__typename!,
                  )}
                  isSmall={['bestItems', 'actualItems'].includes(
                    portalSeeAllMode,
                  )}
                  __typename={item.__typename}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
